/**
 * RouteRecheckButton — re-gather flights for one leg on demand.
 *
 * For when a scrape under-returns (e.g. a date that came back with a single
 * flight). Triggers the fast single-route check and shows the same live
 * "Searching…" → "Found N" progress as a date change, refetching the plan as
 * fares land.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Loader2 } from "lucide-react";
import { routeUpdateApi } from "@/lib/api";
import { useCheckPrices } from "@/hooks/useCheckPrices";

interface Props {
  tripId: string;
  routeId: string;
}

export default function RouteRecheckButton({ tripId, routeId }: Props) {
  const qc = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(null);

  const refetchPlan = () => {
    qc.invalidateQueries({ queryKey: ["tripPlan", tripId] });
    qc.invalidateQueries({ queryKey: ["itinerary", tripId] });
  };

  const check = useCheckPrices(tripId, refetchPlan, jobId);
  const searching = check.phase === "checking";

  const mutation = useMutation({
    mutationFn: () => routeUpdateApi.recheck(routeId),
    onSuccess: (res) => { if (res?.checkJobId) setJobId(res.checkJobId); },
  });

  if (searching) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[hsl(var(--primary))]">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
        {check.status && check.status.flightsFound > 0 ? `Found ${check.status.flightsFound}…` : "Re-searching…"}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      className="inline-flex items-center gap-1 rounded text-[11px] text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
      title="Re-gather flights for this leg"
    >
      <RefreshCw className={"h-3 w-3 " + (mutation.isPending ? "animate-spin" : "")} aria-hidden="true" />
      Re-search
    </button>
  );
}
